<script setup lang="ts">
import device from '@@/bits/device'
import { SearchFilters } from '@@/bits/web_search'
import ImageThumbnail from '@@/library/v4/components/ImageThumbnail.vue'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import {
  DRAGGABLE_STICKER_ELEMENT_TYPE,
  useWhiteboardContentPickerDraggableElement,
} from '@@/vuecomposables/useWhiteboardContentPickerDragAndDrop'
import { preventUnhandled } from '@atlaskit/pragmatic-drag-and-drop/prevent-unhandled'
import type { Emoji } from '@padlet/arvo'
import { processedUrl } from '@padlet/vivaldi-client'
import { computed, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    emoji: Emoji
    darkMode: boolean
    isSelected?: boolean
    iconSize?: number
    hoveredIconSize?: number
    checkmarkSize?: number
    xCheckmark?: boolean
    draggable?: boolean
  }>(),
  {
    isSelected: undefined,
    iconSize: 40,
    hoveredIconSize: undefined,
    checkmarkSize: 14,
    xCheckmark: true,
    draggable: false,
  },
)

defineEmits<{
  (e: 'mouseenter', event: MouseEvent): void
  (e: 'mouseleave'): void
  (e: 'focus', event: FocusEvent): void
  (e: 'blur'): void
  (e: 'pick'): void
}>()

// #region Drag and drop functionality
const buttonRef = ref<InstanceType<typeof OzBaseButton> | undefined>(undefined)
const elementRef = computed(() => buttonRef.value?.$el as HTMLElement | undefined)
const imageRef = ref<InstanceType<typeof ImageThumbnail> | undefined>(undefined)
const imageEl = computed(() => imageRef.value?.$el as HTMLElement | undefined)

const { isDragging } = useWhiteboardContentPickerDraggableElement({
  elementRef,
  type: DRAGGABLE_STICKER_ELEMENT_TYPE,
  data: computed(() => ({
    result: props.emoji,
    searchFilter: SearchFilters.Stickers,
  })),
  isDraggable: computed(() => props.draggable),
  dragPreview: computed(() => {
    if (device.ios || device.safari) return undefined
    const clone = imageEl.value?.cloneNode(true) as HTMLElement | undefined
    if (!clone) return undefined
    if (device.mobile) {
      // Double the size of the drag preview on mobile, as the default size is often
      // equal to our finger size so we can't see the emoji clearly under our finger.
      clone.style.transformOrigin = 'center'
      clone.style.transform = 'scale(2)'
    }
    return clone
  }),
})

// Fix a bug on iOS where the drag preview is not generated correctly (it's the
// same issue in `SurfacePostDraggable`) -- the preview is blank. We hijack the
// `dragstart` event to set a custom drag image.
// This fix requires us to disable the drag preview created by PDND, which is why
// we pass `undefined` to the `dragPreview` option above.
const fixIOSDragPreview = (event: DragEvent): void => {
  if (!device.ios && !device.safari) return

  // Create an img element to use as the drag preview
  const img = document.createElement('img')
  img.style.position = 'absolute'
  img.style.width = `${props.iconSize}px`
  img.style.height = `${props.iconSize}px`
  img.style.backgroundColor = 'transparent'

  // Interestingly, we don't have to double the size of the drag preview here.
  // iOS increases the size automatically (we don't know by how much though).
  img.src = processedUrl(props.emoji.url, {
    width: props.iconSize,
    height: props.iconSize,
    preset: 'icon',
  })

  // Set x and y offsets to 'sort of' center the drag image (not accurate
  // since we don't know how much iOS scales the image).
  event.dataTransfer?.setDragImage(img, props.iconSize, props.iconSize)

  // Remove the delay before the drag preview disappears after a drag ends.
  preventUnhandled.start()
}
// #endregion
</script>

<template>
  <OzBaseButton
    ref="buttonRef"
    :class="[
      'group',
      'relative',
      'flex justify-center items-center',

      'box-border',
      'rounded-xl',
      'bg-transparent',

      // NOTE: Update `estimateItemSize` in EmojiListGrid/EmojiListSingleRow
      // when you change this value.
      'p-1',

      'focus-visible:ring-2',
      {
        'focus-visible:ring-grape-500': !darkMode,
        'focus-visible:ring-canary-500': darkMode,
      },

      isSelected && [
        'ring-2 ring-offset-2',
        {
          'ring-grape-500': !darkMode,
          'ring-canary-500': darkMode,
        },
        {
          'ring-offset-light-ui-100': !darkMode,
          'ring-offset-dark-ui-100': darkMode,
        },
      ],

      isDragging && 'opacity-30',

      {
        'active:bg-button-hover-light': !darkMode,
        'active:bg-button-hover-dark': darkMode,
      },
    ]"
    :data-testid="`emoji-icon-${emoji.code}`"
    :tabindex="0"
    :role="typeof isSelected === 'boolean' ? 'radio' : undefined"
    :aria-label="emoji.name"
    :aria-checked="typeof isSelected === 'boolean' ? String(isSelected) : undefined"
    @mouseenter="$emit('mouseenter', $event)"
    @mouseleave="$emit('mouseleave')"
    @focus="$emit('focus', $event)"
    @blur="$emit('blur')"
    @click="$emit('pick')"
    @keypress.enter="$emit('pick')"
    @keypress.space="$emit('pick')"
    @dragstart.native="fixIOSDragPreview"
  >
    <ImageThumbnail
      ref="imageRef"
      :original-image-width="iconSize"
      :original-image-height="iconSize"
      :width="iconSize"
      :src="processedUrl(emoji.url, { width: iconSize, height: iconSize, preset: 'icon' })"
      :title="emoji.name"
      :class="[
        'emoji-image',
        'transition-transform',
        'origin-center',
        'hover-hover:group-hover:!scale-[var(--hover-scale-factor)]',
      ]"
    />
    <span
      v-if="isSelected && xCheckmark"
      :class="[
        'absolute top-1 start-1',
        'flex items-center justify-center',
        'rounded-xl',
        'h-5 w-5',
        {
          'text-dark-ui-100 bg-canary-500': darkMode,
          'text-light-ui-100 bg-grape-500': !darkMode,
        },
      ]"
    >
      <OzIcon name="checkmark_thicker" :size="checkmarkSize" />
    </span>
  </OzBaseButton>
</template>
